import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface FlowFooterAction {
  label: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  testId?: string;
  id?: string;
  type?: "button" | "submit";
}

interface FlowFooterProps {
  back?: FlowFooterAction;
  cancel?: FlowFooterAction;
  primary?: FlowFooterAction & { variant?: "default" | "destructive" };
  blockedReason?: string;
  secondary?: ReactNode;
  dock?: ReactNode;
  sticky?: boolean;
  isDarkMode?: boolean;
  fullBleed?: boolean;
  className?: string;
}

interface Pin {
  left: number;
  width: number;
  bottom: number;
  height: number;
  padLeft: number;
  padRight: number;
}

const FALLBACK_HEIGHT = 69;

// An overflow box sized by its own content never scrolls or clips: a probe
// taller than the box makes it grow, while a box with a fixed height overflows.
function growsWithContent(node: HTMLElement) {
  const before = node.clientHeight;
  const probe = document.createElement("div");
  probe.dataset.flowFooterProbe = "";
  probe.style.cssText = `flex:none;margin:0;height:${before + 1}px`;
  node.appendChild(probe);
  const grows = node.clientHeight > before;
  probe.remove();
  return grows;
}

// The nearest scrolling ancestor is the panel the bar belongs to; every
// scrolling or clipping ancestor limits how much of that panel is visible.
// Boxes that grow with their content are passed over while a fixed-height
// scroller exists further out (a page that scrolls the document has none).
function findScrollAncestors(el: HTMLElement) {
  const boxes: { node: HTMLElement; scrolls: boolean; grows: boolean }[] = [];
  for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    const scrolls = overflowY === "auto" || overflowY === "scroll";
    if (scrolls || overflowY === "hidden" || overflowY === "clip") {
      boxes.push({ node, scrolls, grows: growsWithContent(node) });
    }
  }
  const fixed = boxes.filter((b) => !b.grows);
  const pool = fixed.some((b) => b.scrolls) ? fixed : boxes;
  return {
    scroller: pool.find((b) => b.scrolls)?.node ?? null,
    clippers: pool.map((b) => b.node),
  };
}

// Sticky cannot hold the bar flush at the end of the scroll panel (its
// padding and containing block get in the way), so the bar is fixed to the
// panel's bottom edge instead and an anchor keeps its place in the flow.
function usePinToScrollPanel(
  enabled: boolean,
  fullBleed: boolean,
  anchorRef: React.RefObject<HTMLDivElement>,
  footerRef: React.RefObject<HTMLElement>,
): Pin | null {
  const [pin, setPin] = useState<Pin | null>(null);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!enabled || !anchor) return;
    const { scroller, clippers } = findScrollAncestors(anchor);
    if (!scroller) {
      setPin(null);
      return;
    }

    const update = () => {
      if (anchor.getClientRects().length === 0) {
        setPin(null);
        return;
      }
      const a = anchor.getBoundingClientRect();
      const s = scroller.getBoundingClientRect();
      const panelWidth = scroller.clientWidth;
      const next: Pin = {
        left: fullBleed ? s.left : a.left,
        width: fullBleed ? panelWidth : a.width,
        bottom: Math.max(
          0,
          window.innerHeight -
            Math.min(window.innerHeight, ...clippers.map((c) => c.getBoundingClientRect().bottom)),
        ),
        height: footerRef.current?.offsetHeight || FALLBACK_HEIGHT,
        padLeft: fullBleed ? Math.max(0, a.left - s.left) : 0,
        padRight: fullBleed ? Math.max(0, s.left + panelWidth - (a.left + a.width)) : 0,
      };
      setPin((prev) =>
        prev &&
        prev.left === next.left &&
        prev.width === next.width &&
        prev.bottom === next.bottom &&
        prev.height === next.height &&
        prev.padLeft === next.padLeft &&
        prev.padRight === next.padRight
          ? prev
          : next,
      );
    };

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(anchor);
    clippers.forEach((c) => observer?.observe(c));
    if (footerRef.current) observer?.observe(footerRef.current);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      observer?.disconnect();
    };
  }, [enabled, fullBleed, anchorRef, footerRef, pin !== null]);

  return pin;
}

export function FlowFooter({
  back,
  cancel,
  primary,
  blockedReason,
  secondary,
  dock,
  sticky = true,
  isDarkMode = false,
  fullBleed = false,
  className,
}: FlowFooterProps) {
  const reasonId = useId();
  const showReason = Boolean(primary?.disabled && blockedReason);
  const anchorRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLElement>(null);
  const pin = usePinToScrollPanel(sticky, fullBleed, anchorRef, footerRef);

  const pinnedStyle: CSSProperties | undefined = pin
    ? {
        position: "fixed",
        left: pin.left,
        width: pin.width,
        bottom: pin.bottom,
        zIndex: 10,
        ...(fullBleed ? { paddingLeft: pin.padLeft, paddingRight: pin.padRight } : {}),
      }
    : undefined;

  const footer = (
    <footer
      ref={footerRef}
      style={pinnedStyle}
      className={cn(
        "flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between",
        dock && "sm:flex-wrap",
        isDarkMode ? "border-gray-700" : "border-gray-200",
        pin && (isDarkMode ? "bg-gray-900 py-3" : "bg-white py-3"),
        className,
      )}
      data-testid="travel-flow-footer"
    >
      {dock && <div className="w-full" data-testid="flow-footer-dock">{dock}</div>}
      {showReason && (
        <p
          id={reasonId}
          className={cn(
            "flex items-start gap-1.5 text-sm sm:max-w-sm",
            isDarkMode ? "text-gray-400" : "text-gray-500",
          )}
          data-testid="travel-flow-blocked-reason"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{blockedReason}</span>
        </p>
      )}
      <div className="flex flex-col-reverse gap-2 sm:ml-auto sm:flex-row sm:items-center sm:justify-end sm:gap-3" data-testid="flow-footer-actions">
        {cancel && (
          <Button
            type={cancel.type ?? "button"}
            variant="ghost"
            id={cancel.id}
            data-testid={cancel.testId}
            disabled={cancel.disabled}
            onClick={cancel.onClick}
            className={cn(
              "min-h-11 w-full sm:w-auto",
              isDarkMode ? "text-gray-300 hover:bg-gray-800" : "text-gray-600",
            )}
          >
            {cancel.label}
          </Button>
        )}
        {back && (
          <Button
            type={back.type ?? "button"}
            variant="outline"
            id={back.id}
            data-testid={back.testId}
            disabled={back.disabled}
            onClick={back.onClick}
            className={cn(
              "min-h-11 w-full sm:w-auto",
              isDarkMode && "border-gray-600 bg-gray-800 text-gray-100 hover:bg-gray-700",
            )}
          >
            {back.label}
          </Button>
        )}
        {secondary}
        {primary && (
          <Button
            type={primary.type ?? "button"}
            variant={primary.variant ?? "default"}
            id={primary.id}
            data-testid={primary.testId}
            disabled={primary.disabled}
            onClick={primary.onClick}
            aria-describedby={showReason ? reasonId : undefined}
            className="min-h-11 w-full sm:w-auto"
          >
            {primary.label}
          </Button>
        )}
      </div>
    </footer>
  );

  if (!sticky) return footer;

  return (
    <div ref={anchorRef} style={pin ? { height: pin.height } : undefined}>
      {pin ? createPortal(footer, document.body) : footer}
    </div>
  );
}
